<script setup>
import { computed, getCurrentInstance, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTheme } from '../composables/useTheme';
import HrDivider from '../primitives/HrDivider.vue';
import HrIcon from '../primitives/HrIcon.vue';
import HrItemLabel from '../primitives/HrItemLabel.vue';
import HrSelect from '../primitives/HrSelect.vue';
import HrListItem from './HrListItem.vue';

/**
 * Single-selection field that renders each option as a rich HrListItem row.
 *
 * Supports flat or grouped option arrays, captions, leading icons, and trailing text. The
 * `option-leading` and `option-trailing` slots take over either end of a row, for a visual the
 * option shape has no field for or a control that acts on that one option.
 * The selected option keeps its leading icon in the closed field, so the field matches
 * the row it was picked from. Setting `displayValue` overrides the field text and drops
 * the icon with it.
 *
 * A value that no option carries never shows raw, since it is usually an id. While `loading` the
 * field shows nothing in its place, as the options may still bring it; once loaded it reads
 * "Unavailable". The value itself is kept rather than cleared: the user chose it, and clearing a
 * value they cannot see would remove that decision without telling them.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Selecting one value from a list where options benefit from supporting text, icons, or trailing metadata
 * @useWhen Selecting one value from a list of plain labels — the rich parts are all optional
 * @avoidWhen Selecting more than one value — use HrMultiSelect for chip-based multi-select
 * @avoidWhen The value may be something no option offers — use HrAutocompleteField
 * @related HrMultiSelect
 * @related HrAutocompleteField
 * @event clear Fired when the user clears the field; modelValue is set to null.
 */
defineOptions({ name: 'HrListSelect' });

/**
 * @typedef {{ label: string, value: *, caption?: string, icon?: string, trailingText?: string,
 *   disable?: boolean, class?: string }} HrListSelectOption
 *   `class` lands on that row only — the dropdown is teleported, so it has to be a global class.
 * @typedef {{ subHeader?: string, options: HrListSelectOption[] }} HrListSelectGroup
 *   A group of options, preceded by a divider (suppressed for the first group) and, with `subHeader`, a header label.
 * @typedef {HrListSelectOption | HrListSelectGroup} HrListSelectEntry
 */

const props = defineProps({
  /** Currently selected value. Supports v-model. */
  modelValue: {
    type: null,
    default: null,
  },
  /**
   * Flat or grouped option list. Each entry is either:
   *   - An option: { label, value, caption?, icon?, trailingText?, disable?, class? }
   *   - A group:   { subHeader, options: [...] }
   * Both can be mixed in the same array.
   * @type {HrListSelectEntry[]}
   * @example [{"label":"Design","caption":"Brand"},{"label":"HR","caption":"People"},{"label":"IT"},{"label":"Legal"}]
   */
  options: {
    type: Array,
    default: () => [],
  },
  /**
   * Property on each option object to emit as the selected value.
   */
  optionValue: {
    type: String,
    default: 'value',
  },
  /**
   * Emits the full option object instead of just the value property.
   * Disables emit-value and map-options on the underlying select.
   */
  emitObject: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders the caption above the label on each option item.
   */
  captionAbove: {
    type: Boolean,
    default: false,
  },
  /**
   * Visual container style.
   * @values filled, outlined
   */
  variant: {
    type: String,
    default: 'outlined',
    validator: (v) => ['filled', 'outlined'].includes(v),
  },
  /**
   * Field label shown above the input.
   * @example Role
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text below the field: one sentence the reader needs to fill it in.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  hint: {
    type: String,
    default: null,
  },
  /**
   * Overrides the text shown for the current selection in the closed field.
   * Use when the closed field needs detail that shouldn't repeat on every dropdown option.
   */
  displayValue: {
    type: String,
    default: null,
  },
  /** Shows a clear button when the field has a value. */
  clearable: {
    type: Boolean,
    default: false,
  },
  /** Shows a text input in the trigger for filtering options. */
  useInput: {
    type: Boolean,
    default: false,
  },
  /**
   * Hides the selected value's chip/label so the typed filter text shows in its place.
   * Rendering only: combine with `useInput` and `fillInput` for the single-line type-to-filter
   * look. The value still resolves to one of `options` — this field has no way to accept typed
   * text that is not in the list.
   */
  hideSelected: {
    type: Boolean,
    default: false,
  },
  /**
   * Fills the input with the selected option's label after selection instead of clearing it.
   * See `hideSelected` for the type-to-filter combination.
   */
  fillInput: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Prevents changes while maintaining a readable appearance. */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Shows a loading indicator in the field. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Applies error styling. */
  error: {
    type: Boolean,
    default: false,
  },
  /** Error text shown below the field when `error` is true. */
  errorMessage: {
    type: String,
    default: null,
  },
  /** Validation rule functions; each receives the current value and returns true or an error string. */
  rules: {
    type: Array,
    default: null,
  },
  /** Delays validation until the field loses focus. */
  lazyRules: {
    type: Boolean,
    default: false,
  },
  /** Hides the bottom space reserved for hint/error text. */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /** Reduces vertical padding for compact layouts. */
  dense: {
    type: Boolean,
    default: false,
  },
  /**
   * Suppresses the trailing checkmark on the selected option.
   * Use when options have trailingText, as the checkmark overrides the trailing slot.
   */
  noCheckmark: {
    type: Boolean,
    default: false,
  },
  /**
   * Quasar visual props forwarded to the underlying select. Ignored in the design-system theme.
   * @deprecated Will be removed when the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the selected value changes. */
  'update:modelValue': null,
  /**
   * Asks the caller to narrow `options`; receives (value, update, abort) and the list does not change
   * until `update` runs. It also fires once as the menu opens, so a field whose options come from a
   * query can load them on first use instead of on every render of the form around it.
   */
  filter: null,
});

const attrs = useAttrs();
const instance = getCurrentInstance();
const selectRef = ref(null);

function forwardFilter(...args) {
  emit('filter', ...args);
}

/**
 * The filter listener, but only when the caller is listening for it.
 *
 * QSelect reads a filter listener as "this caller narrows the options": opening the menu starts a
 * filter, and the field stays loading with no menu until the caller answers with `update`. Passing
 * one on regardless would hang every field that narrows nothing, which is most of them. The
 * caller's own listener is not in `useAttrs`, which drops the listeners of declared emits, and it
 * is read per render so attaching or dropping one takes effect.
 */
function filterListeners() {
  return instance.vnode.props?.onFilter ? { filter: forwardFilter } : {};
}

defineExpose({
  /** Closes the options dropdown programmatically. */
  hidePopup: () => selectRef.value?.hidePopup(),
  /** Runs the field's validation rules; returns a boolean or a Promise resolving to one. */
  validate: (...args) => selectRef.value?.validate(...args),
});
const { hasTheme } = useTheme();
const { t } = useI18n();

const flattenedOptions = computed(() => props.options.flatMap((entry, index) => {
  const isGroup = entry !== null && typeof entry === 'object' && Array.isArray(entry.options);
  if (!isGroup) {
    return [entry];
  }
  const divider = index === 0 ? [] : [{ _divider: true, disable: true }];
  const header = entry.subHeader == null
    ? []
    : [{ _header: true, label: entry.subHeader, disable: true }];
  return [...divider, ...header, ...entry.options];
}));

// With `map-options`, QSelect hands `selected-item` the raw value when no option carries it.
function isUnresolved(opt) {
  return (opt === null) || (typeof opt !== 'object');
}

const effectiveProps = computed(() => {
  const base = {
    modelValue: props.modelValue,
    options: flattenedOptions.value,
    optionValue: props.optionValue,
    optionLabel: 'label',
    emitValue: !props.emitObject,
    mapOptions: !props.emitObject,
    variant: props.variant,
    ...(props.label !== null && { label: props.label }),
    ...(props.hint !== null && { hint: props.hint }),
    ...(props.errorMessage !== null && { errorMessage: props.errorMessage }),
    ...(props.displayValue !== null && { displayValue: props.displayValue }),
    clearable: props.clearable,
    useInput: props.useInput,
    hideSelected: props.hideSelected,
    fillInput: props.fillInput,
    disable: props.disable,
    readonly: props.readonly,
    loading: props.loading,
    // The spinner is not announced, so a screen reader would otherwise read the field as settled.
    'aria-busy': props.loading || null,
    error: props.error,
    ...(props.rules !== null && { rules: props.rules }),
    lazyRules: props.lazyRules,
    hideBottomSpace: props.hideBottomSpace,
    dense: props.dense,
  };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, legacyProps: props.legacyProps, ...attrs };
});
</script>

<template>
  <hr-select
    ref="selectRef"
    v-bind="effectiveProps"
    v-on="filterListeners()"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <!-- Withheld entirely when `displayValue` is set and no caller slot overrides it: QSelect only
         falls back to `display-value` if `selected-item` is absent, and it renders the fallback even
         with nothing selected, which a slot returning empty scope would not. -->
    <template
      v-if="$slots['selected-item'] || displayValue === null"
      #selected-item="scope"
    >
      <!-- @slot selected-item Custom rendering of the selected value in the field; receives Quasar scope props. -->
      <slot
        v-if="$slots['selected-item']"
        name="selected-item"
        v-bind="scope ?? {}"
      />
      <template v-else-if="isUnresolved(scope.opt)">
        <span
          v-if="!loading"
          class="ellipsis hr-list-select__unavailable"
        >{{ t('common.labelUnavailable') }}</span>
      </template>
      <template v-else>
        <hr-icon
          v-if="scope.opt.icon"
          :name="scope.opt.icon"
          class="hr-list-select__selected-icon"
        />
        <span class="ellipsis">{{ scope.opt.label ?? '' }}</span>
      </template>
    </template>

    <template #option="scope">
      <hr-divider v-if="scope.opt._divider" />
      <hr-item-label
        v-else-if="scope.opt._header"
        header
      >
        {{ scope.opt.label }}
      </hr-item-label>
      <hr-list-item
        v-else
        v-bind="scope.itemProps"
        :class="scope.opt.class ?? null"
        :label="scope.opt.label ?? null"
        :caption="scope.opt.caption ?? null"
        :icon="scope.opt.icon ?? null"
        :trailing-text="scope.opt.trailingText ?? null"
        :caption-above="captionAbove"
      >
        <template
          v-if="$slots['option-leading']"
          #leading
        >
          <!-- @slot option-leading Leading visual per option (e.g. flag); receives `{ opt }`. Replaces `icon`. -->
          <slot
            name="option-leading"
            :opt="scope.opt"
          />
        </template>
        <template
          v-if="$slots['option-trailing'] || (!noCheckmark && scope.selected)"
          #trailing
        >
          <!--
            Metadata, or a control acting on the option. It owns the trailing area, so it replaces
            both `trailingText` and the selected checkmark — `selected` is passed so a caller taking
            it over can still render one.
          -->
          <!-- @slot Trailing content per option; receives `{ opt, selected }`. -->
          <slot
            v-if="$slots['option-trailing']"
            name="option-trailing"
            :opt="scope.opt"
            :selected="scope.selected"
          />
          <hr-icon
            v-else
            name="check"
          />
        </template>
      </hr-list-item>
    </template>

    <template
      v-if="$slots['no-option']"
      #no-option
    >
      <!-- @slot Shown when the filtered option list is empty. -->
      <slot name="no-option" />
    </template>
    <template
      v-if="$slots.prepend"
      #prepend
    >
      <!-- @slot Content prepended inside the input field. -->
      <slot name="prepend" />
    </template>
    <template
      v-if="$slots.append"
      #append
    >
      <!-- @slot Content appended inside the input field. -->
      <slot name="append" />
    </template>
  </hr-select>
</template>

<style lang="scss" scoped>
/*
 * Matches the leading-icon gap of the option rows in the dropdown.
 *
 * The color has to be set explicitly: this icon sits inside `.q-field__native`, which HrSelect
 * colors `--app-on-surface` for the value text, so the icon would otherwise inherit the full-
 * emphasis text color. Every other icon in the field and list vocabulary is on-surface-variant
 * — the field's own prepend/append icons and the leading section of the option rows — so the
 * selected icon matches the row it was picked from rather than the text beside it.
 */
.hr-list-select__selected-icon {
  margin-right: 8px;
  color: var(--app-on-surface-variant);
}

/* Lower emphasis than a real selection, so it reads as a status rather than a value. */
.hr-list-select__unavailable {
  color: var(--app-on-surface-variant);
}

/*
 * HrSelect dims the field's label, text and marginal icons when disabled. The selected icon
 * carries an explicit color, so it needs the same treatment or it would stay full-strength.
 */
.q-field--disabled .hr-list-select__selected-icon,
.q-field--disabled .hr-list-select__unavailable {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}
</style>
